import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const factoryQuotesJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "工厂专属报价中心 | 皇冠鸿运商城",
  "description": "皇冠鸿运商城工厂专属报价中心，查看买家采购需求，提交专业报价，仅限认证工厂会员访问。",
  "url": `${SITE_URL}/factory-quotes`,
  "noIndex": true
};

// Mock RFQ data visible only to verified paid factory members
const rfqList = [
  {
    id: "RFQ-2026-001",
    title: "大批量采购不锈钢保温杯",
    category: "家居用品",
    quantity: "5000件",
    truckLoads: "3柜 40HQ",
    targetPrice: "¥28–35/件",
    deadline: "2026-05-15",
    shippingPort: "广州港",
    paymentTerms: "30% 定金，70% 见提单",
    postedAt: "2026-04-08",
    description: "需要采购不锈钢真空保温杯，容量500ml，需要自有品牌定制，要求通过SGS质检，提供样品。买家为欧洲进口商，长期合作意向。",
    specs: ["容量：500ml", "材质：304不锈钢", "颜色：哑光黑/银色/白色", "需要定制LOGO", "需SGS质检报告", "包装：彩盒，每箱12个"],
    buyerRegion: "华南",
    urgency: "普通",
    quotesReceived: 4,
    isNew: true,
  },
  {
    id: "RFQ-2026-002",
    title: "户外运动背包大批量采购",
    category: "运动户外",
    quantity: "2000件",
    truckLoads: "2柜 40GP",
    targetPrice: "¥45–60/件",
    deadline: "2026-05-20",
    shippingPort: "上海港",
    paymentTerms: "信用证 L/C",
    postedAt: "2026-04-09",
    description: "采购户外登山背包，容量30L，需防水面料，自有品牌定制。买家为国内户外品牌，有稳定订单需求。",
    specs: ["容量：30L", "材质：防水尼龙600D", "颜色：军绿/黑色/橙色", "需定制品牌标签", "需提供样品", "交货期：45天内"],
    buyerRegion: "华东",
    urgency: "紧急",
    quotesReceived: 7,
    isNew: true,
  },
  {
    id: "RFQ-2026-003",
    title: "儿童玩具积木套装采购",
    category: "母婴玩具",
    quantity: "10000套",
    truckLoads: "5柜 40HQ",
    targetPrice: "¥18–25/套",
    deadline: "2026-05-30",
    shippingPort: "深圳港",
    paymentTerms: "平台托管付款",
    postedAt: "2026-04-07",
    description: "采购儿童益智积木套装，需符合EN71欧洲玩具安全标准，提供CE认证。买家为欧洲玩具连锁零售商。",
    specs: ["材质：ABS无毒塑料", "颜色：多色", "需CE认证", "需EN71测试报告", "年龄适用：3岁以上", "包装：彩盒，每箱6套"],
    buyerRegion: "华南",
    urgency: "普通",
    quotesReceived: 12,
    isNew: false,
  },
  {
    id: "RFQ-2026-004",
    title: "LED工作灯大批量采购",
    category: "家用电器",
    quantity: "8000件",
    truckLoads: "4柜 40GP",
    targetPrice: "¥35–50/件",
    deadline: "2026-06-10",
    shippingPort: "宁波港",
    paymentTerms: "电汇 T/T",
    postedAt: "2026-04-06",
    description: "采购便携式LED工作灯，需要充电款，亮度2000流明以上，需通过CE、RoHS认证。",
    specs: ["亮度：2000流明以上", "电池：锂电池，续航8小时", "防护等级：IP65", "需CE、RoHS认证", "颜色：黑色/黄色"],
    buyerRegion: "华东",
    urgency: "普通",
    quotesReceived: 3,
    isNew: false,
  },
  {
    id: "RFQ-2026-005",
    title: "有机棉T恤大批量采购",
    category: "服装鞋包",
    quantity: "15000件",
    truckLoads: "整车 × 8",
    targetPrice: "¥12–18/件",
    deadline: "2026-05-25",
    shippingPort: "广州港",
    paymentTerms: "30% 定金，70% 见提单",
    postedAt: "2026-04-05",
    description: "采购有机棉圆领T恤，需GOTS认证，多色多码，买家为欧洲快时尚品牌，长期合作。",
    specs: ["材质：100%有机棉", "克重：180g/㎡", "尺码：XS-3XL", "颜色：白/黑/灰/藏青", "需GOTS认证", "需提供面料检测报告"],
    buyerRegion: "华南",
    urgency: "紧急",
    quotesReceived: 18,
    isNew: false,
  },
];

const urgencyColors: Record<string, string> = {
  "紧急": "bg-red-100 text-red-600",
  "普通": "bg-gray-100 text-gray-500",
};

export default function FactoryQuotesPage() {
  const navigate = useNavigate();
  const [isVerified] = useState(true); // Simulated: factory is verified paid member
  const [selectedRfq, setSelectedRfq] = useState<typeof rfqList[0] | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [filterCategory, setFilterCategory] = useState("全部");
  const [quoteForm, setQuoteForm] = useState({ price: "", leadTime: "", moq: "", note: "" });

  const categories = ["全部", ...Array.from(new Set(rfqList.map((r) => r.category)))];

  const filtered = filterCategory === "全部"
    ? rfqList
    : rfqList.filter((r) => r.category === filterCategory);

  const handleQuoteSubmit = () => {
    setQuoteSuccess(true);
    setTimeout(() => {
      setQuoteSuccess(false);
      setQuoteOpen(false);
      setQuoteForm({ price: "", leadTime: "", moq: "", note: "" });
    }, 2500);
  };

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-10 max-w-md mx-4 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-5">
            <i className="ri-lock-line text-red-400 text-4xl"></i>
          </div>
          <h2 className="text-gray-800 font-bold text-xl mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            仅限认证工厂会员访问
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            买家采购报价信息属于商业机密，仅对通过平台严格认证并已缴纳会员费的工厂会员开放。
            请先完成工厂认证并激活会员资格。
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            申请工厂认证
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="工厂专属报价中心 | 买家采购需求 | 皇冠鸿运商城"
        description="皇冠鸿运商城工厂专属报价中心，查看买家采购需求，提交专业报价，仅限认证工厂会员访问。"
        keywords="工厂报价,采购需求,认证工厂,皇冠鸿运商城"
        canonical="/factory-quotes"
        jsonLd={factoryQuotesJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>皇冠鸿运商城 — 工厂专属报价中心</span>
            <span className="bg-yellow-400 text-gray-900 text-xs px-2 py-0.5 rounded-full font-bold">
              <i className="ri-shield-check-line mr-0.5"></i>已认证工厂
            </span>
          </div>
          <a href="/" className="hover:underline cursor-pointer flex items-center gap-1">
            <i className="ri-home-line"></i> 返回首页
          </a>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png" alt="Logo" className="h-9 w-auto object-contain" />
              <span className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>皇冠鸿运商城</span>
            </a>
            <span className="text-gray-300 hidden sm:block">|</span>
            <span className="text-gray-600 text-sm font-medium hidden sm:block">工厂专属报价中心</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              <i className="ri-shield-check-line text-green-500 text-sm"></i>
              <span className="text-green-600 text-xs font-medium">黄金工厂会员 · 已认证</span>
            </div>
            <button
              onClick={() => navigate("/reseller-dashboard")}
              className="text-gray-500 hover:text-red-500 text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-dashboard-line mr-1"></i>我的后台
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Access notice */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-5 mb-8 text-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-lock-unlock-line text-white text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-lg" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                工厂专属 · 买家采购报价信息
              </h2>
              <p className="text-white/80 text-sm mt-1">
                以下采购需求由真实买家发布，信息对公众完全隐藏，仅您（已认证付费工厂会员）可见。
                请认真阅读需求，提交专业报价，平台将保护您的报价信息安全。
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white/70 text-xs">当前可见报价</p>
              <p className="text-3xl font-bold">{rfqList.length}</p>
              <p className="text-white/70 text-xs">条采购需求</p>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span className="text-gray-500 text-sm font-medium whitespace-nowrap">按品类筛选：</span>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterCategory === cat
                  ? "bg-red-500 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* RFQ list */}
        <div className="space-y-4">
          {filtered.map((rfq) => (
            <div key={rfq.id} className="bg-white rounded-2xl p-6 hover:border-red-200 border border-transparent transition-all">
              <div className="flex flex-col lg:flex-row gap-5">
                {/* Left: main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {rfq.isNew && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">NEW</span>
                    )}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${urgencyColors[rfq.urgency]}`}>
                      {rfq.urgency}
                    </span>
                    <span className="bg-orange-100 text-orange-600 text-xs px-2.5 py-1 rounded-full">{rfq.category}</span>
                    <span className="text-gray-300 text-xs">{rfq.id}</span>
                    <span className="text-gray-400 text-xs">发布于 {rfq.postedAt}</span>
                  </div>

                  <h3 className="text-gray-800 font-bold text-lg mb-2">{rfq.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{rfq.description}</p>

                  {/* Key specs preview */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {rfq.specs.slice(0, 3).map((spec) => (
                      <span key={spec} className="bg-gray-50 text-gray-600 text-xs px-3 py-1 rounded-lg border border-gray-100">
                        {spec}
                      </span>
                    ))}
                    {rfq.specs.length > 3 && (
                      <span className="text-gray-400 text-xs px-2 py-1">+{rfq.specs.length - 3} 项</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { icon: "ri-stack-line", label: "采购量", value: rfq.quantity },
                      { icon: "ri-truck-line", label: "整柜/整车", value: rfq.truckLoads },
                      { icon: "ri-price-tag-3-line", label: "目标价", value: rfq.targetPrice },
                      { icon: "ri-calendar-line", label: "截止日期", value: rfq.deadline },
                    ].map((item) => (
                      <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className={`${item.icon} text-gray-400 text-xs`}></i>
                          <span className="text-gray-400 text-xs">{item.label}</span>
                        </div>
                        <p className="text-gray-800 font-bold text-sm">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                    <span><i className="ri-ship-line mr-1"></i>{rfq.shippingPort}</span>
                    <span><i className="ri-bank-card-line mr-1"></i>{rfq.paymentTerms}</span>
                    <span><i className="ri-map-pin-line mr-1"></i>买家地区：{rfq.buyerRegion}</span>
                    <span><i className="ri-chat-3-line mr-1"></i>已收到 {rfq.quotesReceived} 个报价</span>
                  </div>
                </div>

                {/* Right: action */}
                <div className="flex flex-col gap-3 lg:w-44 flex-shrink-0 justify-center">
                  <button
                    onClick={() => { setSelectedRfq(rfq); setQuoteOpen(true); }}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-1"></i>提交报价
                  </button>
                  <button
                    onClick={() => setSelectedRfq(rfq)}
                    className="w-full border border-gray-200 hover:bg-gray-50 text-gray-600 py-3 rounded-xl font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-eye-line mr-1"></i>查看详情
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail / Quote Modal */}
      {(selectedRfq || quoteOpen) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => { setSelectedRfq(null); setQuoteOpen(false); setQuoteSuccess(false); }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-xl mx-4 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-gray-800 font-bold text-lg">
                {quoteOpen ? "提交报价" : "采购需求详情"}
              </h3>
              <button
                onClick={() => { setSelectedRfq(null); setQuoteOpen(false); setQuoteSuccess(false); }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6">
              {quoteSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-green-500 text-4xl"></i>
                  </div>
                  <p className="text-gray-800 font-bold text-xl mb-2">报价已提交！</p>
                  <p className="text-gray-500 text-sm">买家将在24小时内查看您的报价，请保持联系方式畅通</p>
                </div>
              ) : quoteOpen && selectedRfq ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4 mb-2">
                    <p className="text-gray-600 font-semibold text-sm">{selectedRfq.title}</p>
                    <p className="text-gray-400 text-xs mt-1">采购量：{selectedRfq.quantity} · 目标价：{selectedRfq.targetPrice}</p>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs font-medium block mb-1.5">您的报价（/单位） <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={quoteForm.price}
                      onChange={(e) => setQuoteForm({ ...quoteForm, price: e.target.value })}
                      placeholder="例：¥30/件（含税）"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">交货期 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={quoteForm.leadTime}
                        onChange={(e) => setQuoteForm({ ...quoteForm, leadTime: e.target.value })}
                        placeholder="例：30天"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                      />
                    </div>
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">最小起订量（MOQ）</label>
                      <input
                        type="text"
                        value={quoteForm.moq}
                        onChange={(e) => setQuoteForm({ ...quoteForm, moq: e.target.value })}
                        placeholder="例：1000件"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs font-medium block mb-1.5">补充说明</label>
                    <textarea
                      value={quoteForm.note}
                      onChange={(e) => setQuoteForm({ ...quoteForm, note: e.target.value })}
                      placeholder="可说明您的工厂优势、质检资质、样品政策等..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 resize-none"
                    />
                  </div>
                  <button
                    onClick={handleQuoteSubmit}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-1"></i>提交报价
                  </button>
                </div>
              ) : selectedRfq ? (
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${urgencyColors[selectedRfq.urgency]}`}>{selectedRfq.urgency}</span>
                      <span className="bg-orange-100 text-orange-600 text-xs px-2.5 py-1 rounded-full">{selectedRfq.category}</span>
                    </div>
                    <h4 className="text-gray-800 font-bold text-xl mb-2">{selectedRfq.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{selectedRfq.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "采购数量", value: selectedRfq.quantity },
                      { label: "整柜/整车", value: selectedRfq.truckLoads },
                      { label: "目标价格", value: selectedRfq.targetPrice },
                      { label: "截止日期", value: selectedRfq.deadline },
                      { label: "装运港口", value: selectedRfq.shippingPort },
                      { label: "付款方式", value: selectedRfq.paymentTerms },
                    ].map((item) => (
                      <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-gray-400 text-xs mb-1">{item.label}</p>
                        <p className="text-gray-800 font-semibold text-sm">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-gray-700 font-semibold text-sm mb-2">产品规格要求</p>
                    <div className="space-y-1.5">
                      {selectedRfq.specs.map((spec) => (
                        <div key={spec} className="flex items-center gap-2">
                          <i className="ri-checkbox-blank-circle-fill text-red-400 text-xs flex-shrink-0"></i>
                          <span className="text-gray-600 text-sm">{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setQuoteOpen(true)}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-1"></i>立即提交报价
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
